import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';
import renderGroups from 'utils/renderGroups';
import Button from 'common/Button';
import Spinner from 'common/Spinner';
import RawHtml from 'common/RawHtml';
import pageActions from 'core/page/actions';
import backButtonStyles from 'pages/styles/backButton';
import { withStyles } from 'theme/withStyles';
import ErrorMsg from 'common/ErrorMsg';
import MessageAnnounce from 'a11y/MessageAnnounce';

import rootStyles from './styles/root';

const styles = (theme) => {
  const common = {
    root: rootStyles(theme),
  };
  return {
    ...common,
    '@global': {
      '.btn-row-group': {
        display: 'none !important',
      },
    },
    back: backButtonStyles(theme),
    button: {
      margin: [0, 0, theme.spacing.xs, 0],
    },
    buttonRow: {
      display: 'block !important',
      margin: '0 auto',
      maxWidth: theme.forms.maxWidth,
      padding: [0, theme.spacing.lg],

      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        display: 'none',
      },
    },
    time: {
      '& .label': {
        fontSize: 18,
        padding: [0, 0, 0, 5],
      },
      '& .value': {
        fontWeight: 500,
      },
      '&:first-of-type': {
        '& .value': {
          borderRight: `1px solid ${theme.palette.text}`,
        },

        paddingLeft: 45,
      },
      '&:last-child': {
        paddingRight: 45,
      },
      display: 'flex',
      flexDirection: 'column-reverse',

      float: 'left',

      fontFamily: theme.typography.h1.fontFamily,
      fontSize: 36,
      width: '50%',
    },
    timer: {
      '& h2': {
        padding: [0, 0, theme.spacing.xs, 0],
      },
      background: theme.palette.grey.light,
      borderRadius: 10,
      color: theme.palette.text,
      padding: theme.spacing.xs,
      position: 'fixed',
      right: 50,
      textAlign: 'center',
      top: 200,
      width: 210,
      zIndex: 10,

      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        borderRadius: 0,
        float: 'left',
        marginTop: `-${theme.spacing.md}px`,
        padding: [theme.spacing.unit, theme.spacing.xs],
        position: 'static',
        right: 'auto',
        top: 'auto',
        width: '100%',
      },
    },
    timerInnerWrap: {
      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        '& $time': {
          '&:first-of-type': {
            '& dd': {
              borderRight: 'none',
            },
            borderRight: `1px solid ${theme.palette.text}`,
            paddingLeft: 0,
            paddingRight: theme.spacing.xs,
          },
          '&:last-child': {
            textAlign: 'left',
          },

          alignItems: 'center',
          flexDirection: 'row-reverse',
        },
        '& h2': {
          padding: 0,
          width: '70%',
        },

        alignItems: 'center',

        display: 'flex',
      },
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'column-reverse',
      margin: [theme.spacing.xl, 0, 0, 0],
    },
  };
};

export const KbaBare = ({
  appLoading,
  backAllowed,
  backButtonLabel,
  classes,
  continueAllowed,
  currentTimeRemaining,
  disableButton,
  fields,
  getStatus,
  groups,
  headerHtml,
  kbaStatus,
  loading,
  name,
  onBack,
  onSave,
  saveAndContinueAllowed,
  saveButtonLabel,
  submitButtonLabel,
  submitQuestions,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(currentTimeRemaining);
  const [showSrMessage, setShowSrMessage] = useState(true);
  const duration = moment.duration(timeRemaining * 1000);
  const maxDuration = moment.duration(currentTimeRemaining * 1000);

  useEffect(() => {
    getStatus();
  }, [getStatus]);

  useEffect(() => {
    if (kbaStatus === 'SUCCESS' || kbaStatus === 'SUCCESS_WITH_QUESTIONS') {
      setTimeRemaining(currentTimeRemaining);
      window.scrollTo(0, 0);
    }
  }, [kbaStatus, currentTimeRemaining]);

  useEffect(() => {
    let timeout;

    if (!appLoading && Object.keys(fields).length) {
      timeout = setTimeout(() => {
        if (timeRemaining > 0) {
          setTimeRemaining((r) => r - 1);
        } else {
          submitQuestions();
        }
      }, 1000);
    }

    return () => clearTimeout(timeout);
  }, [fields, appLoading, timeRemaining, submitQuestions]);

  useEffect(() => {
    if (
      currentTimeRemaining - 2 === timeRemaining ||
      duration.asSeconds() % 30 === 0 ||
      duration.asSeconds() === 15 ||
      duration.asSeconds() <= 5
    ) {
      setShowSrMessage(true);
    } else {
      setShowSrMessage(false);
    }
  }, [currentTimeRemaining, duration, timeRemaining]);

  const getSrMessage = () => {
    if (currentTimeRemaining - 2 === timeRemaining) {
      return `${maxDuration.minutes()} minutes ${maxDuration.seconds()} seconds remaining`;
    }
    if (duration.minutes() > 0) {
      return `${duration.minutes()} minutes ${duration.seconds()} seconds remaining`;
    }
    if (duration.seconds() > 5) {
      return `${duration.seconds()} seconds remaining`;
    }

    return duration.seconds();
  };

  return (
    <div className="kba-view">
      <RawHtml>{headerHtml}</RawHtml>

      {timeRemaining > 0 && !appLoading && (
        <aside className={classnames('kba-timer', classes.timer)} role="presentation">
          <div className={classnames('wrapper', classes.timerInnerWrap)}>
            <h2>Time remaining:</h2>
            <div className={classnames('time', classes.time)}>
              <div className="label">min</div>
              <div className="value minutes">{duration.minutes()}</div>
            </div>
            <div className={classnames('time', classes.time)}>
              <div className="label">sec</div>
              <div className="value seconds">{duration.seconds()}</div>
            </div>
            <MessageAnnounce show={showSrMessage} message={getSrMessage()} isRemoved />
          </div>
        </aside>
      )}

      <ErrorMsg type="page" />

      <div className="row">
        <div className="widget">
          <div className="region-groups">{renderGroups(groups)}</div>
        </div>
      </div>

      <div className={classnames('row btn-row-page', `btn-page-${name}`, classes.buttonRow)}>
        <div className={classnames('col-lg-12', classes.wrapper)}>
          {backAllowed && !appLoading && (
            <Button
              className={classnames(
                'btn btn-mobile btn-tertiary btn-page-back',
                classes.button,
                classes.back,
              )}
              data-name="btn-page-back"
              onClick={onBack}
              variant="secondary"
              disabled={loading}
            >
              <RawHtml>{backButtonLabel}</RawHtml>
            </Button>
          )}
          {saveAndContinueAllowed && !appLoading && (
            <Button
              className={classnames(
                'btn btn-mobile btn-tertiary btn-save-and-continue',
                classes.button,
              )}
              data-name="btn-save-and-continue"
              disabled={disableButton}
              onClick={onSave}
              variant="secondary"
            >
              <RawHtml>{saveButtonLabel}</RawHtml>
            </Button>
          )}
          {!appLoading && continueAllowed && (
            <Button
              className={classnames(
                `btn btn-mobile btn-page-next btn-next-${name}`,
                classes.button,
                { loading },
              )}
              data-name="btn-page-next"
              disabled={disableButton}
              onClick={submitQuestions}
            >
              <RawHtml>{submitButtonLabel}</RawHtml>
              <i className="icon-arrow-thin-right" aria-hidden="true" />
              <Spinner loading={loading} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

KbaBare.propTypes = {
  appLoading: PropTypes.bool,
  backAllowed: PropTypes.bool,
  backButtonLabel: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  continueAllowed: PropTypes.bool,
  currentTimeRemaining: PropTypes.number,
  disableButton: PropTypes.bool.isRequired,
  fields: PropTypes.shape({}).isRequired,
  getStatus: PropTypes.func.isRequired,
  groups: PropTypes.shape({}),
  headerHtml: PropTypes.string,
  kbaStatus: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveAndContinueAllowed: PropTypes.bool,
  saveButtonLabel: PropTypes.string,
  submitButtonLabel: PropTypes.string,
  submitQuestions: PropTypes.func.isRequired,
};

KbaBare.defaultProps = {
  appLoading: true,
  backAllowed: false,
  backButtonLabel: null,
  continueAllowed: true,
  currentTimeRemaining: 0,
  groups: {},
  headerHtml: null,
  kbaStatus: '',
  loading: false,
  saveAndContinueAllowed: false,
  saveButtonLabel: null,
  submitButtonLabel: null,
};

const mapStateToProps = ({ configs, data, fields, groups, page, session, ui }) => ({
  appLoading: ui.appLoading,
  backAllowed: page.backAllowed,
  backButtonLabel: page.backButtonLabel,
  continueAllowed: page.continueAllowed,
  currentTimeRemaining: configs?.kbaTimeout,
  fields,
  groups,
  headerHtml: page.headerHtml,
  kbaStatus: data.kbaFetchOrSubmitStatus,
  loading: ui.pageLoading && session.meta.configs.kba_spinny,
  name: page.name,
  saveAndContinueAllowed: page.saveAndContinueAllowed,
  saveButtonLabel: page.saveButtonLabel,
  submitButtonLabel: page.submitButtonLabel,
});

const mapDispatchToProps = {
  getStatus: () => pageActions.status('kba/questions'),
  onBack: pageActions.back,
  onSave: pageActions.save,
  submitQuestions: pageActions.next,
};

const KbaStyled = withStyles(styles)(KbaBare);

export default connect(mapStateToProps, mapDispatchToProps)(KbaStyled);
